/**
 * Etiketi doğrudan canvas'a çizer.
 *
 * NEDEN AYRI BİR ÇİZİCİ
 * Uzun süre tek çizim yolu HTML'di: etiket DOM'da kurulup html2canvas ile
 * rasterlenirdi (hem PDF hem termal yazıcı çıktısı). Ölçüm bu yolun
 * kullanılamaz olduğunu gösterdi — html2canvas her çağrıda gizli bir iframe
 * kurup belgenin bütün CSS'ini (MUI/emotion ile binlerce kural) yeniden
 * ayrıştırıyor ve DOM'u kendi kodu ile yeniden çiziyor: çağrı başına ~4 sn
 * sabit, etiket başına ~0,5 sn. 100 etiketlik bir PDF 114 saniye sürüyordu.
 * Tarayıcının kendi çizim yolu (foreignObject) ise daha da yavaş çıktı ve boş
 * sayfa üretti.
 *
 * Buradaki çizici aynı işi etiket başına birkaç milisaniyede yapıyor, çünkü
 * DOM, CSS ve iframe hiç devreye girmiyor: elemanlar (metin, barkod, QR,
 * görsel, kutu, çizgi) canvas 2D çağrılarına bire bir karşılık geliyor.
 *
 * EKRAN İLE TUTARLILIK
 * Önizleme ve tarayıcı yazdırma hâlâ HTML (LabelHtmlRenderer) — orada metin
 * vektör kalır ve yazıcı kendi çözünürlüğünde basar. Bu dosya HTML'in görsel
 * davranışını taklit eder: mm ölçüleri, kelime kaydırma, kutuya sığdırma,
 * `object-fit: contain`, `box-sizing: border-box`. İki yol ayrışmasın diye
 * ölçü ve renk kararları tek kaynaktan (LabelElement) okunuyor.
 */

import {
  LabelElement,
  LabelTemplate,
  PlaceholderData,
  getDesignSize,
  isElementVisible,
  resolvePlaceholders,
} from '../../../types/labelTemplate.types';
import { resolveImageSrc } from '../assets/domesticBadge';
import { getCachedFittedBarcodeCanvas, getCachedQrCanvas } from '../utils/barcodeRenderer';

/** Görsel yüklemesi bu süreyi aşarsa eleman boş bırakılır */
const IMAGE_TIMEOUT_MS = 4000;

/**
 * Yüklenmiş görseller. Aynı logo yüzlerce etikette tekrarlanıyor; her seferinde
 * yeniden yüklemek ağ isteği ve kod çözme demek.
 */
const imageCache = new Map<string, HTMLImageElement | null>();

/**
 * Görseli yükler. Başarısızlıkta null döner — tek bir kırık görsel yüzünden
 * baskının tamamı iptal olmasın.
 *
 * crossOrigin şart: resim kütüphanesi ayrı origin'de (img.*). CORS izni
 * olmadan yüklenen görsel tuvali "kirletir"; sonrasında ne getImageData
 * (termal baskı eşiklemesi) ne toDataURL (PDF'e gömme) çalışır.
 */
function loadImage(url: string): Promise<HTMLImageElement | null> {
  const cached = imageCache.get(url);
  if (cached !== undefined) return Promise.resolve(cached);

  return new Promise((resolve) => {
    const img = new Image();
    let settled = false;

    const finish = (result: HTMLImageElement | null) => {
      if (settled) return;
      settled = true;
      imageCache.set(url, result);
      resolve(result);
    };

    const timer = setTimeout(() => finish(null), IMAGE_TIMEOUT_MS);
    img.onload = () => {
      clearTimeout(timer);
      finish(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      finish(null);
    };

    img.crossOrigin = 'anonymous';
    img.src = url;
  });
}

/**
 * Çizimden önce çağrılmalı: eleman görsellerini yükler, barkod/QR'ları üretir.
 *
 * Çizimin kendisi senkron olmak zorunda — bir sayfanın elemanları arasında
 * `await` olsa her etiket için tarayıcıya dönüp gelmek gerekirdi. Bu yüzden
 * tüm asenkron iş (ağdan görsel çekme) burada, tek seferde bitiriliyor.
 */
export async function preloadLabelAssets(
  template: LabelTemplate,
  dataList: Array<PlaceholderData | undefined>,
): Promise<void> {
  const urls = new Set<string>();

  for (const data of dataList) {
    for (const el of template.elements) {
      if (el.type !== 'image' || !el.src) continue;
      if (!isElementVisible(el, data)) continue;
      const url = resolveImageSrc(el.src);
      if (url) urls.add(url);
    }
  }

  await Promise.all([...urls].map(loadImage));
}

/** Metni kutu genişliğine göre satırlara böler (HTML word-wrap davranışı) */
function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];

  for (const paragraph of text.split('\n')) {
    if (!paragraph) {
      lines.push('');
      continue;
    }

    let line = '';
    for (const word of paragraph.split(/\s+/)) {
      const candidate = line ? `${line} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxWidth || !line) {
        // Tek kelime kutudan genişse yine de satıra alınır; kırma aşağıda
        line = candidate;
        continue;
      }
      lines.push(line);
      line = word;
    }
    if (line) lines.push(line);
  }

  // `word-break: break-word` karşılığı: sığmayan tek kelimeyi karakterden kır
  const out: string[] = [];
  for (const line of lines) {
    if (ctx.measureText(line).width <= maxWidth || line.length <= 1) {
      out.push(line);
      continue;
    }
    let chunk = '';
    for (const ch of line) {
      if (chunk && ctx.measureText(chunk + ch).width > maxWidth) {
        out.push(chunk);
        chunk = ch;
      } else {
        chunk += ch;
      }
    }
    if (chunk) out.push(chunk);
  }

  return out;
}

function drawTextElement(
  ctx: CanvasRenderingContext2D,
  el: LabelElement,
  text: string,
  pxPerMm: number,
): void {
  if (!text) return;

  const boxW = el.widthMm * pxPerMm;
  const boxH = el.heightMm * pxPerMm;
  const lineRatio = el.lineHeight ?? 1.2;
  const family = el.fontFamily ?? 'Arial';
  const weight = el.bold ? 'bold' : 'normal';
  const style = el.italic ? 'italic' : 'normal';
  const minPx = 0.6 * pxPerMm;

  let fontPx = (el.fontSizeMm ?? 3) * pxPerMm;
  const applyFont = () => {
    ctx.font = `${style} ${weight} ${fontPx}px ${family}`;
  };

  applyFont();
  let lines = wrapLines(ctx, text, boxW);

  // Kutuya sığdırma — HTML tarafındaki ShrinkText ile aynı oransal yakınsama
  if (el.autoShrink) {
    for (let i = 0; i < 12; i++) {
      const needed = lines.length * fontPx * lineRatio;
      if (needed <= boxH || fontPx <= minPx) break;
      const next = Math.max(minPx, fontPx * (boxH / needed) * 0.96);
      if (Math.abs(next - fontPx) < 0.02 * pxPerMm) break;
      fontPx = next;
      applyFont();
      lines = wrapLines(ctx, text, boxW);
    }
  }

  const lineH = fontPx * lineRatio;
  const align = el.align ?? 'left';

  ctx.fillStyle = el.color ?? '#000000';
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  const x = align === 'center' ? boxW / 2 : align === 'right' ? boxW : 0;

  lines.forEach((line, i) => {
    const top = i * lineH;
    // `overflow: hidden` karşılığı — kutudan taşan satır basılmaz
    if (top >= boxH) return;
    ctx.fillText(line, x, top + lineH / 2);
  });
}

/** HTML'deki `object-fit: contain` karşılığı */
function drawContained(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  sourceW: number,
  sourceH: number,
  boxW: number,
  boxH: number,
): void {
  if (sourceW <= 0 || sourceH <= 0) return;
  const ratio = Math.min(boxW / sourceW, boxH / sourceH);
  const w = sourceW * ratio;
  const h = sourceH * ratio;
  ctx.drawImage(source, (boxW - w) / 2, (boxH - h) / 2, w, h);
}

function drawElement(
  ctx: CanvasRenderingContext2D,
  el: LabelElement,
  data: PlaceholderData | undefined,
  pxPerMm: number,
): void {
  const boxW = el.widthMm * pxPerMm;
  const boxH = el.heightMm * pxPerMm;

  ctx.save();
  ctx.globalAlpha = el.opacity ?? 1;
  ctx.translate(el.xMm * pxPerMm, el.yMm * pxPerMm);
  if (el.rotation) {
    // HTML'de transform-origin sol üst; canvas'ta da öteleme sonrası orijin orada
    ctx.rotate((el.rotation * Math.PI) / 180);
  }

  switch (el.type) {
    case 'text':
      drawTextElement(ctx, el, resolvePlaceholders(el.text ?? '', data), pxPerMm);
      break;

    case 'barcode': {
      const value = resolvePlaceholders(el.barcodeValue ?? '', data);
      // Barkod doğrudan hedef çözünürlükte üretilir; ara ölçek çubuk
      // kalınlıklarını bozuyor (bkz. generateFittedBarcodeCanvas)
      const canvas = getCachedFittedBarcodeCanvas(value, {
        format: el.barcodeFormat ?? 'CODE128',
        boxWidthPx: boxW,
        boxHeightPx: boxH,
        showText: el.showBarcodeText ?? true,
        color: el.color ?? '#000000',
      });

      if (canvas) {
        const smoothing = ctx.imageSmoothingEnabled;
        // Yeniden örnekleme gri ara tonlar üretir; termalde eşiklendiğinde
        // çubuk kenarları kayar
        ctx.imageSmoothingEnabled = false;
        // Yatayda 1:1 (canvas.width), yalnızca dikeyde kutuya uzatılır —
        // dikey esneme çubuk kalınlığını etkilemiyor
        ctx.drawImage(canvas, Math.round((boxW - canvas.width) / 2), 0, canvas.width, boxH);
        ctx.imageSmoothingEnabled = smoothing;
      }
      break;
    }

    case 'qr': {
      const value = resolvePlaceholders(el.qrValue ?? '', data);
      const canvas = getCachedQrCanvas(value, {
        size: 512,
        errorLevel: el.qrErrorLevel ?? 'M',
        color: el.color ?? '#000000',
      });
      // QR kare olmalı; HTML tarafı da kısa kenarı esas alıyor
      const side = Math.min(boxW, boxH);
      if (canvas) ctx.drawImage(canvas, 0, 0, side, side);
      break;
    }

    case 'image': {
      const url = resolveImageSrc(el.src);
      const img = url ? imageCache.get(url) : null;
      if (img) drawContained(ctx, img, img.naturalWidth, img.naturalHeight, boxW, boxH);
      break;
    }

    case 'rect': {
      const stroke = (el.strokeWidthMm ?? 0.3) * pxPerMm;
      const radius = (el.cornerRadiusMm ?? 0) * pxPerMm;

      // roundRect eski tarayıcılarda yok; köşe yuvarlama olmadan çizilir
      const rounded = ctx as CanvasRenderingContext2D & {
        roundRect?: (x: number, y: number, w: number, h: number, r: number) => void;
      };

      const path = () => {
        ctx.beginPath();
        // Kenar çizgisi kutunun içinde kalmalı — box-sizing: border-box
        if (radius > 0 && typeof rounded.roundRect === 'function') {
          rounded.roundRect(stroke / 2, stroke / 2, boxW - stroke, boxH - stroke, radius);
        } else {
          ctx.rect(stroke / 2, stroke / 2, boxW - stroke, boxH - stroke);
        }
      };

      if (el.fillColor && el.fillColor !== 'transparent') {
        ctx.fillStyle = el.fillColor;
        path();
        ctx.fill();
      }
      if (stroke > 0) {
        ctx.strokeStyle = el.strokeColor ?? '#000000';
        ctx.lineWidth = stroke;
        path();
        ctx.stroke();
      }
      break;
    }

    case 'line':
      ctx.fillStyle = el.fillColor ?? '#000000';
      // Sıfır yükseklikte çizgi görünmez; HTML tarafı da alt sınır uyguluyor
      ctx.fillRect(0, 0, boxW, Math.max(0.1 * pxPerMm, boxH));
      break;

    default:
      break;
  }

  ctx.restore();
}

/**
 * Tasarım katmanını fiziksel etiket alanına oturtan döndürme.
 * LabelHtmlRenderer'daki CSS dönüşümünün canvas karşılığı: döndürme sonrası
 * içerik alan dışına taştığı için telafi ötelemesi gerekiyor.
 */
function applyTemplateRotation(
  ctx: CanvasRenderingContext2D,
  rotation: number,
  designWidthPx: number,
  designHeightPx: number,
): void {
  switch (rotation) {
    case 90:
      ctx.translate(designHeightPx, 0);
      ctx.rotate(Math.PI / 2);
      break;
    case 180:
      ctx.translate(designWidthPx, designHeightPx);
      ctx.rotate(Math.PI);
      break;
    case 270:
      ctx.translate(0, designWidthPx);
      ctx.rotate(-Math.PI / 2);
      break;
    default:
      break;
  }
}

export interface LabelDrawOptions {
  /** 1 mm kaç piksel — hedef çözünürlük buradan gelir (dpi / 25.4) */
  pxPerMm: number;
  /**
   * Şablon döndürmesi uygulanmaz, etiket tasarım ölçüsünde çizilir.
   * Termal baskıda döndürme mürekkep matrisi üzerinde yapılıyor (rotateMono).
   */
  skipRotation?: boolean;
}

/** Çizilecek alanın piksel ölçüsü — çağıran tuvali buna göre kurmalı */
export function getLabelCanvasSize(
  template: LabelTemplate,
  options: LabelDrawOptions,
): { width: number; height: number } {
  const design = getDesignSize(template);
  const widthMm = options.skipRotation ? design.widthMm : template.labelWidthMm;
  const heightMm = options.skipRotation ? design.heightMm : template.labelLengthMm;
  return {
    width: Math.max(1, Math.round(widthMm * options.pxPerMm)),
    height: Math.max(1, Math.round(heightMm * options.pxPerMm)),
  };
}

/**
 * Tek etiketi çizer. Orijin etiketin sol üst köşesi kabul edilir; çağıran
 * sayfa içindeki konuma öteleme yapmış olmalı.
 *
 * Senkron: görseller `preloadLabelAssets` ile önceden yüklenmiş olmalı.
 */
export function drawLabel(
  ctx: CanvasRenderingContext2D,
  template: LabelTemplate,
  data: PlaceholderData | undefined,
  options: LabelDrawOptions,
): void {
  const { pxPerMm } = options;
  const design = getDesignSize(template);
  const size = getLabelCanvasSize(template, options);

  ctx.save();

  // Etiket zemini ve taşan içeriğin kırpılması
  ctx.beginPath();
  ctx.rect(0, 0, size.width, size.height);
  ctx.clip();
  ctx.fillStyle = template.backgroundColor ?? '#ffffff';
  ctx.fillRect(0, 0, size.width, size.height);

  if (!options.skipRotation) {
    applyTemplateRotation(ctx, template.rotation, design.widthMm * pxPerMm, design.heightMm * pxPerMm);
  }

  for (const el of template.elements) {
    if (!isElementVisible(el, data)) continue;
    drawElement(ctx, el, data, pxPerMm);
  }

  ctx.restore();
}
